// hooks/useNotifications.js
import { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import apiClient from '../api/client';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  

  // Fetch counts separately
  const fetchCounts = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/v1/notifications/counts');
      console.log('📊 Counts response:', response.data);
      setUnreadCount(response.data.unread || 0);
    } catch (err) {
      console.error('❌ Error fetching counts:', err);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async (options = {}) => {
    const { 
      limit = 20, 
      offset = 0, 
      append = false,
      category = null,
      priority = null,
      unreadOnly = false 
    } = options;

    try {
      setLoading(true);
      setError(null);
      
      const params = { limit, offset };
      if (category) params.category = category;
      if (priority) params.priority = priority;
      if (unreadOnly) params.unreadOnly = true;

      console.log('🔍 Fetching notifications with params:', params);
      
      const response = await apiClient.get('/api/v1/notifications', { params });
      
      console.log('📋 Notifications response:', response.data);
      
      const responseData = Array.isArray(response.data?.notifications) 
        ? response.data.notifications 
        : Array.isArray(response.data) 
          ? response.data 
          : [];
      
      if (append) {
        setNotifications(prev => [...(Array.isArray(prev) ? prev : []), ...responseData]);
      } else {
        setNotifications(responseData);
      }
      
    } catch (err) {
      console.error('❌ Error fetching notifications:', err);
      setError('Failed to fetch notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []); // Remove fetchCounts dependency

  

  // Mark as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await apiClient.patch(`/api/v1/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await apiClient.patch('/api/v1/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  }, []);

  // Dismiss notification
  const dismissNotification = useCallback(async (notificationId) => {
    try {
      await apiClient.patch(`/api/v1/notifications/${notificationId}/dismiss`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      
      // Update unread count if it was unread
      const notification = notifications.find(n => n.notification_id === notificationId);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  }, [notifications]);

  // Format notification time
  const formatNotificationTime = useCallback((dateString) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  }, []);

  // Get notification icon (placeholder)
  const getNotificationIcon = useCallback(() => {
    // Return icon name based on category/type
    return 'Notifications';
  }, []);

  // Get notification color
  const getNotificationColor = useCallback((priority) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  }, []);

  // Set error
  const setErrorState = useCallback((errorMessage) => {
    setError(errorMessage);
  }, []);

  // Initial load
  useEffect(() => {
    fetchNotifications();
    fetchCounts();
  }, [fetchNotifications, fetchCounts]);

  // Socket listeners
  useEffect(() => {
    const socket = window.socket;
    if (socket) {
      socket.on('new-notification', (notification) => {
        console.log('🔔 Socket: New notification received', notification);
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
      });

      socket.on('notification-counts-updated', (counts) => {
        console.log('📊 Socket: Counts updated', counts);
        setUnreadCount(counts.unread || 0);
      });

      return () => {
        socket.off('new-notification');
        socket.off('notification-counts-updated');
      };
    }
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    setError: setErrorState,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    formatNotificationTime,
    getNotificationIcon,
    getNotificationColor
  };
};